import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import {
  RetroHeader,
  HeroSection,
  TrendingStrip,
  RetroLoading,
  RetroPokemonCard,
  HighScoresModal,
} from "./RetroComponents";
import { useT } from "./i18n";

const API_BASE = process.env.REACT_APP_POKEAPI_BASE || "https://pokeapi.co/api/v2";
const TCG_API_BASE = process.env.REACT_APP_TCG_API_BASE || "https://api.pokemontcg.io/v2";
const TCG_API_KEY = process.env.REACT_APP_POKEMON_TCG_API_KEY;

// Smart backend URL detection - works in both preview and production
const getBackendUrl = () => {
  // If explicitly set in env, use it
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Otherwise, use same origin (works for both preview and production)
  return window.location.origin;
};

// Pokemon Types for filtering
const POKEMON_TYPES = [
  'normal', 'fire', 'water', 'electric', 'grass', 'ice', 'fighting', 'poison',
  'ground', 'flying', 'psychic', 'bug', 'rock', 'ghost', 'dragon', 'dark', 'steel', 'fairy'
];

// Pokemon Generations
const POKEMON_GENERATIONS = [
  { name: 'Generation I', range: [1, 151] },
  { name: 'Generation II', range: [152, 251] },
  { name: 'Generation III', range: [252, 386] },
  { name: 'Generation IV', range: [387, 493] },
  { name: 'Generation V', range: [494, 649] },
  { name: 'Generation VI', range: [650, 721] },
  { name: 'Generation VII', range: [722, 809] },
  { name: 'Generation VIII', range: [810, 905] },
  { name: 'Generation IX', range: [906, 1025] }
];

// Pokemon TCG Service with comprehensive search strategies
const pokemonTcgApi = {
  // Helper function to clean and format Pokemon names for better searching
  formatPokemonName(name) {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
      .replace(/\s+/g, ' ')     // Normalize spaces
      .trim();
  },

  // Helper function to generate name variations for better search results
  generateNameVariations(pokemonName) {
    const formatted = this.formatPokemonName(pokemonName);
    const variations = [
      formatted,                           // Original formatted name
      formatted.replace(/\s/g, ''),       // No spaces
      formatted.replace(/-/g, ' '),       // Hyphens to spaces
      formatted.replace(/-/g, ''),        // Remove hyphens
      formatted.charAt(0).toUpperCase() + formatted.slice(1), // Capitalize first letter
    ];

    // Add common Pokemon name variations
    const commonVariations = {
      'nidoran-f': ['nidoran♀', 'nidoran female'],
      'nidoran-m': ['nidoran♂', 'nidoran male'],
      'mr-mime': ['mr. mime', 'mrmime'],
      'farfetchd': ['farfetch\'d', 'farfetch\'d'],
      'ho-oh': ['ho oh', 'hooh'],
      'porygon-z': ['porygon z', 'porygonz'],
      'mime-jr': ['mime jr', 'mime jr.'],
      'type-null': ['type null', 'typenull'],
      'tapu-koko': ['tapu koko', 'tapukoko'],
      'tapu-lele': ['tapu lele', 'tapulele'],
      'tapu-bulu': ['tapu bulu', 'tapubulu'],
      'tapu-fini': ['tapu fini', 'tapufini'],
      // Generation IX specific variations (Pokemon 900-1025)
      'wo-chien': ['wo chien', 'wochien'],
      'chien-pao': ['chien pao', 'chienpao'],
      'ting-lu': ['ting lu', 'tinglu'],
      'chi-yu': ['chi yu', 'chiyu'],
      'roaring-moon': ['roaring moon', 'roaringmoon'],
      'iron-valiant': ['iron valiant', 'ironvaliant'],
      'walking-wake': ['walking wake', 'walkingwake'],
      'iron-leaves': ['iron leaves', 'ironleaves'],
      'gouging-fire': ['gouging fire', 'gougingfire'],
      'raging-bolt': ['raging bolt', 'ragingbolt'],
      'iron-boulder': ['iron boulder', 'ironboulder'],
      'iron-crown': ['iron crown', 'ironcrown'],
      'great-tusk': ['great tusk', 'greattusk'],
      'scream-tail': ['scream tail', 'screamtail'],
      'brute-bonnet': ['brute bonnet', 'brutebonnet'],
      'flutter-mane': ['flutter mane', 'fluttermane'],
      'slither-wing': ['slither wing', 'slitherwing'],
      'sandy-shocks': ['sandy shocks', 'sandyshocks'],
      'iron-treads': ['iron treads', 'irontreads'],
      'iron-bundle': ['iron bundle', 'ironbundle'],
      'iron-hands': ['iron hands', 'ironhands'],
      'iron-jugulis': ['iron jugulis', 'ironjugulis'],
      'iron-moth': ['iron moth', 'ironmoth'],
      'iron-thorns': ['iron thorns', 'ironthorns'],
    };

    if (commonVariations[formatted]) {
      variations.push(...commonVariations[formatted]);
    }

    // Remove duplicates and return unique variations
    return [...new Set(variations)];
  },

  async searchCardsByQuery(query, page = 1, pageSize = 12) {
    try {
      console.log(`Searching TCG with query: ${query} via backend proxy`);
      
      // Use backend proxy endpoint instead of direct API call
      const backendUrl = getBackendUrl();
      const proxyUrl = `${backendUrl}/api/pokemon-tcg-proxy`;
      
      const response = await axios.get(proxyUrl, {
        params: {
          q: query,
          page,
          pageSize,
          orderBy: '-set.releaseDate',
          select: 'id,name,images,set,rarity,tcgplayer,cardmarket'
        }
      });
      
      console.log(`TCG API response for "${query}":`, response.data?.data?.length || 0, 'cards found');
      return response.data;
    } catch (error) {
      console.error(`Error fetching TCG cards with query "${query}":`, error.response?.status, error.message);
      return { data: [], totalCount: 0 };
    }
  },

  async getCardsByPokemon(pokemonName, page = 1, pageSize = 24) {
    try {
      console.log(`🔍 SIMPLIFIED SEARCH: Starting search for ${pokemonName}`);
      
      // SIMPLE AND RELIABLE: Just try basic search patterns
      const searchPatterns = [
        `name:${pokemonName}*`,                    // Exact name with wildcard
        `name:"${pokemonName}"`,                   // Exact quoted name
        `name:${pokemonName.toLowerCase()}*`,      // Lowercase with wildcard
        `name:*${pokemonName}*`                    // Wildcard both sides
      ];

      let allCards = [];
      let searchIndex = 0;

      for (const pattern of searchPatterns) {
        searchIndex++;
        console.log(`📡 API CALL ${searchIndex}: Trying pattern "${pattern}"`);
        
        const result = await this.searchCardsByQuery(pattern, 1, 12);
        
        if (result.data && result.data.length > 0) {
          console.log(`✅ SUCCESS: Found ${result.data.length} cards with pattern "${pattern}"`);
          allCards = result.data;
          break; // Stop on first success
        } else {
          console.log(`❌ FAILED: No cards found with pattern "${pattern}"`);
        }
      }

      // Final processing
      if (allCards.length > 0) {
        const processedResult = this.processAndReturnCards(allCards, allCards.length, page, pageSize);
        console.log(`🎉 FINAL RESULT: Returning ${processedResult.data.length} cards for ${pokemonName}`);
        return processedResult;
      } else {
        console.log(`😞 NO RESULTS: No cards found for ${pokemonName} after trying all patterns`);
        return { data: [], totalCount: 0, page, pageSize };
      }

    } catch (error) {
      console.error(`💥 CRITICAL ERROR in getCardsByPokemon for ${pokemonName}:`, error);
      return { data: [], totalCount: 0, page, pageSize };
    }
  },

  // Helper function to process and return cards (reduces code duplication)
  processAndReturnCards(allCards, totalCount, page, pageSize) {
    // Remove duplicates based on card ID
    const uniqueCards = allCards.reduce((unique, card) => {
      const exists = unique.find(existing => existing.id === card.id);
      if (!exists) {
        unique.push(card);
      }
      return unique;
    }, []);

    // Sort by release date (newest first) and limit results
    const sortedCards = uniqueCards
      .sort((a, b) => {
        const dateA = new Date(a.set?.releaseDate || '1900-01-01');
        const dateB = new Date(b.set?.releaseDate || '1900-01-01');
        return dateB - dateA;
      })
      .slice(0, pageSize);

    console.log(`Processed ${sortedCards.length} cards for Pokemon search`);
    
    return {
      data: sortedCards,
      totalCount: Math.max(totalCount, sortedCards.length),
      page,
      pageSize
    };
  }
};

// L3 Dex Logo Component
const L3DexLogo = () => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <div className="w-12 h-12 bg-gradient-to-br from-blue-800 to-blue-900 rounded-full flex items-center justify-center border-2 border-yellow-400 shadow-lg">
        <div className="text-yellow-400 text-xl font-bold">🐉</div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
          <div className="text-blue-800 text-xs font-bold">👑</div>
        </div>
      </div>
      <div className="absolute bottom-0 right-0 w-6 h-6 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full border-2 border-white flex items-center justify-center">
        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
      </div>
    </div>
    <div className="text-3xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
      L3 DEX
    </div>
  </div>
);

// Login/Profile Component
const UserProfile = ({ user, onClose, onLogin, onLogout }) => {
  const { t } = useT();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    profileImage: '🧑‍💼',
    bio: '',
    favoriteType: '',
    location: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLogin) {
      onLogin({
        name: formData.name || 'Trainer',
        email: formData.email,
        isLoggedIn: true,
        level: 15,
        badges: 8,
        pokemonCaught: 150,
        cardsCollected: 45,
        ...formData
      });
    } else {
      onLogin({
        name: formData.name,
        email: formData.email,
        isLoggedIn: true,
        level: 1,
        badges: 0,
        pokemonCaught: 0,
        cardsCollected: 0,
        ...formData
      });
    }
  };

  const inputClass =
    "w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none focus:bg-cream dark:focus:bg-nightsurface placeholder:text-ink/40 dark:placeholder:text-white/40";

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-50"
      data-testid="user-profile-modal"
      onClick={onClose}
    >
      <div
        className="panel-retro max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-pokered dark:bg-neonpink text-white p-3 sm:p-6 border-b-[3px] border-ink dark:border-neoncyan overflow-hidden">
          <div className="crt-overlay" />
          <div className="flex justify-between items-start relative z-10 gap-3">
            <h2 className="font-pixel text-2xl sm:text-4xl leading-none tracking-wide">
              {user?.isLoggedIn
                ? t("profile.title.your")
                : isLogin
                ? t("profile.title.login")
                : t("profile.title.register")}
            </h2>
            <button
              onClick={onClose}
              data-testid="profile-close-button"
              className="font-pixel text-3xl bg-white text-pokered hover:bg-cream border-[3px] border-ink w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-[4px_4px_0_0_#111] dark:shadow-[4px_4px_0_0_#00f0ff] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all leading-none shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="overflow-y-auto bg-cream dark:bg-nightbg p-4 sm:p-7 text-ink dark:text-white">
          {user?.isLoggedIn ? (
            <div>
              <div className="text-center mb-6">
                <div className="panel-retro inline-flex w-24 h-24 items-center justify-center text-4xl mb-3" data-testid="profile-avatar">
                  {user.profileImage || '👤'}
                </div>
                <h3 className="font-pixel text-3xl capitalize text-ink dark:text-white leading-none" data-testid="profile-name">{user.name}</h3>
                <p className="mt-2 font-mono text-xs uppercase tracking-widest text-ink/60 dark:text-white/60" data-testid="profile-email">{user.email}</p>
                {user.location && (
                  <p className="mt-1 font-mono text-xs uppercase tracking-widest text-ink/50 dark:text-white/50" data-testid="profile-location">
                    📍 {user.location}
                  </p>
                )}
              </div>

              {user.bio && (
                <div className="panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm p-3 mb-5" data-testid="profile-bio">
                  <p className="font-mono text-sm italic text-ink/80 dark:text-white/80">"{user.bio}"</p>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { value: user.level, label: t("profile.stat.level"), testid: "profile-stat-level" },
                  { value: user.badges, label: t("profile.stat.badges"), testid: "profile-stat-badges" },
                  { value: user.pokemonCaught, label: t("profile.stat.pokemon"), testid: "profile-stat-pokemon" },
                  { value: user.cardsCollected, label: t("profile.stat.cards"), testid: "profile-stat-cards" },
                ].map((s) => (
                  <div key={s.testid} className="panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm p-3 text-center" data-testid={s.testid}>
                    <div className="font-pixel text-3xl text-pokered dark:text-neonpink leading-none">{s.value}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-ink/60 dark:text-white/60">{s.label}</div>
                  </div>
                ))}
              </div>

              <button
                onClick={onLogout}
                data-testid="profile-logout-button"
                className="btn-retro btn-primary w-full !text-xl !py-3"
              >
                {t("profile.logout")}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="profile-form">
              <div className="flex gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  data-testid="profile-tab-login"
                  className={`btn-retro flex-1 !text-base ${isLogin ? 'btn-primary' : ''}`}
                >
                  {t("profile.tab.login")}
                </button>
                <button
                  type="button"
                  onClick={() => setIsLogin(false)}
                  data-testid="profile-tab-register"
                  className={`btn-retro flex-1 !text-base ${!isLogin ? 'btn-secondary' : ''}`}
                >
                  {t("profile.tab.register")}
                </button>
              </div>

              {!isLogin && (
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("profile.input.name")}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className={inputClass}
                    data-testid="profile-input-name"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("profile.input.email")}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={inputClass}
                  data-testid="profile-input-email"
                  required
                />
              </div>

              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("profile.input.password")}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={inputClass}
                  data-testid="profile-input-password"
                  required
                />
              </div>

              {!isLogin && (
                <>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("profile.input.bio")}</label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData({...formData, bio: e.target.value})}
                      className={inputClass}
                      data-testid="profile-input-bio"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("profile.input.location")}</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className={inputClass}
                      data-testid="profile-input-location"
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                data-testid="profile-submit-button"
                className="btn-retro btn-primary w-full !text-xl !py-3 mt-2"
              >
                {isLogin ? t("profile.submit.login") : t("profile.submit.register")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Advanced Filter Component
const PokemonFilters = ({ filters, onFiltersChange, totalCount }) => {
  const { t } = useT();
  // Expanded by default on tablet/desktop; collapsed on mobile to save vertical space
  const [isExpanded, setIsExpanded] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= 768
  );

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      type: '',
      generation: '',
      minId: '',
      maxId: '',
      sortBy: 'id'
    });
  };

  return (
    <div className="panel-retro p-4 sm:p-5 mb-5 sm:mb-6" data-testid="pokemon-filters">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2">
        <h3 className="font-pixel text-2xl sm:text-3xl text-ink dark:text-neoncyan tracking-wide flex items-center gap-2">
          <span className="text-pokered dark:text-neonpink">▣</span> {t("filters.title")}
        </h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="btn-retro btn-secondary !text-base"
          data-testid="filters-toggle"
        >
          {isExpanded ? t("filters.hide") : t("filters.show")}
        </button>
      </div>

      <div className="text-center mb-4">
        <span className="inline-block led-price text-sm" data-testid="filters-total-count">
          {t("filters.found", { n: totalCount })}
        </span>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("filters.search.label")}</label>
              <input
                type="text"
                placeholder={t("filters.search.placeholder")}
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                data-testid="filter-search"
                className="w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none focus:bg-cream dark:focus:bg-nightsurface placeholder:text-ink/40 dark:placeholder:text-white/40"
              />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("filters.type.label")}</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                data-testid="filter-type"
                className="retro-select w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none capitalize"
              >
                <option value="">{t("filters.type.all")}</option>
                {POKEMON_TYPES.map((type) => (
                  <option key={type} value={type} className="capitalize">
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("filters.gen.label")}</label>
              <select
                value={filters.generation}
                onChange={(e) => handleFilterChange("generation", e.target.value)}
                data-testid="filter-generation"
                className="retro-select w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none"
              >
                <option value="">{t("filters.gen.all")}</option>
                {POKEMON_GENERATIONS.map((gen, index) => (
                  <option key={index} value={index + 1}>
                    {gen.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("filters.minId")}</label>
              <input
                type="number"
                placeholder="1"
                min="1"
                max="1025"
                value={filters.minId}
                onChange={(e) => handleFilterChange("minId", e.target.value)}
                data-testid="filter-min-id"
                className="w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none"
              />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("filters.maxId")}</label>
              <input
                type="number"
                placeholder="1025"
                min="1"
                max="1025"
                value={filters.maxId}
                onChange={(e) => handleFilterChange("maxId", e.target.value)}
                data-testid="filter-max-id"
                className="w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none"
              />
            </div>

            <div>
              <label className="block font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70 mb-1">{t("filters.sort.label")}</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                data-testid="filter-sort-by"
                className="retro-select w-full px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none"
              >
                <option value="id">{t("filters.sort.id")}</option>
                <option value="name">{t("filters.sort.nameAsc")}</option>
                <option value="nameDesc">{t("filters.sort.nameDesc")}</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                data-testid="filters-clear"
                className="btn-retro btn-primary w-full !text-base"
              >
                {t("filters.clear")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Trading Cards Component with Price Sources
const TradingCards = ({ pokemonName, priceSource, onPriceSourceChange }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (pokemonName) {
      fetchCards();
    }
  }, [pokemonName]);

  const fetchCards = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await pokemonTcgApi.getCardsByPokemon(pokemonName);
      setCards(response.data || []);
    } catch (err) {
      setError("Failed to load cards. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (card) => {
    // Handle both TCGPlayer and Cardmarket pricing
    if (priceSource === 'cardmarket') {
      // First try to get actual Cardmarket prices if available
      if (card.cardmarket?.prices) {
        const prices = card.cardmarket.prices;
        let price = null;
        
        if (prices.averageSellPrice) price = prices.averageSellPrice;
        else if (prices.lowPrice) price = prices.lowPrice;
        else if (prices.trendPrice) price = prices.trendPrice;
        
        if (price) return `€${price.toFixed(2)}`;
      }
      
      // Fallback: Convert TCGPlayer prices to EUR as approximation
      if (card.tcgplayer?.prices) {
        const tcgPrices = card.tcgplayer.prices;
        let tcgPrice = null;
        
        if (tcgPrices.holofoil?.market) tcgPrice = tcgPrices.holofoil.market;
        else if (tcgPrices.normal?.market) tcgPrice = tcgPrices.normal.market;
        else if (tcgPrices.reverseHolofoil?.market) tcgPrice = tcgPrices.reverseHolofoil.market;
        
        if (tcgPrice) {
          const euroPrice = (tcgPrice * 0.85).toFixed(2); // USD to EUR approximation
          return `€${euroPrice} (est.)`;
        }
      }
    } else {
      // TCGPlayer pricing
      if (card.tcgplayer?.prices) {
        const prices = card.tcgplayer.prices;
        let price = null;
        
        if (prices.holofoil?.market) price = prices.holofoil.market;
        else if (prices.normal?.market) price = prices.normal.market;
        else if (prices.reverseHolofoil?.market) price = prices.reverseHolofoil.market;
        
        if (price) return `$${price.toFixed(2)}`;
      }
    }
    
    return null;
  };

  const getRarityColor = (rarity) => {
    const rarityColors = {
      'Common': 'bg-gray-500',
      'Uncommon': 'bg-green-500',
      'Rare': 'bg-blue-500',
      'Rare Holo': 'bg-purple-500',
      'Ultra Rare': 'bg-yellow-500',
      'Secret Rare': 'bg-red-600'
    };
    return rarityColors[rarity] || 'bg-gray-400';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-red-500 mx-auto mb-4"></div>
        <p className="text-gray-600 font-semibold">Loading trading cards...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4 font-semibold">{error}</p>
        <button 
          onClick={fetchCards}
          className="px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 font-bold transition-colors shadow-lg"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 text-center bg-gradient-to-r from-red-100 to-blue-100 rounded-2xl p-4">
        <p className="text-gray-800 font-bold text-lg">🎴 Trading Cards for <span className="text-red-600 capitalize">{pokemonName}</span></p>
        
        {/* Price Source Toggle */}
        <div className="mt-4 flex justify-center gap-4">
          <button
            onClick={() => onPriceSourceChange('tcgplayer')}
            className={`px-6 py-2 rounded-full font-bold transition-colors ${
              priceSource === 'tcgplayer' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            💰 TCGPlayer ($)
          </button>
          <button
            onClick={() => onPriceSourceChange('cardmarket')}
            className={`px-6 py-2 rounded-full font-bold transition-colors ${
              priceSource === 'cardmarket' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🇪🇺 CardMarket (€)
          </button>
        </div>
      </div>
      
      {cards.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {cards.map((card) => (
            <div
              key={card.id}
              className="bg-gradient-to-br from-yellow-50 to-blue-50 border-3 border-yellow-400 rounded-xl p-3 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:border-red-400"
            >
              <div className="text-center">
                <img
                  src={card.images?.small || '/placeholder-card.png'}
                  alt={card.name}
                  className="w-full h-auto rounded-lg mb-3 shadow-lg border-2 border-white"
                  loading="lazy"
                />
                <h4 className="text-sm font-bold text-gray-800 mb-1">{card.name}</h4>
                <p className="text-xs text-gray-600 mb-2">{card.set?.name}</p>
                
                {card.rarity && (
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white shadow-md mb-2 ${getRarityColor(card.rarity)}`}>
                    ⭐ {card.rarity}
                  </span>
                )}
                
                {formatPrice(card) && (
                  <div className="mt-2">
                    <span className="text-sm font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                      {formatPrice(card)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gradient-to-br from-yellow-50 to-red-50 rounded-2xl border-2 border-yellow-300">
          <div className="text-6xl mb-4">🎴</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No cards found for {pokemonName}</h3>
          <p className="text-gray-600 mb-4">
            Our comprehensive search couldn't find any trading cards for this Pokémon.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            This could mean the Pokémon doesn't have trading cards yet, or they use a different name in the TCG.
          </p>
          <button 
            onClick={fetchCards}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-bold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
          >
            🔄 Search Again
          </button>
        </div>
      )}
    </div>
  );
};

// Pokemon Grid Component — Retro / Arcade restyle
const PokemonGrid = ({ pokemon, onPokemonSelect }) => {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 sm:gap-4 p-3 sm:p-6"
      data-testid="pokemon-grid"
    >
      {pokemon.map((poke) => (
        <RetroPokemonCard
          key={poke.id}
          pokemon={poke}
          onClick={() => onPokemonSelect(poke)}
        />
      ))}
    </div>
  );
};

// Pokemon Detail Modal with Trading Cards
const PokemonDetail = ({ pokemon, onClose }) => {
  const { t } = useT();
  const [pokemonData, setPokemonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    const fetchPokemonDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE}/pokemon/${pokemon.id}`);
        setPokemonData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching pokemon details:", error);
        setLoading(false);
      }
    };

    if (pokemon) {
      fetchPokemonDetails();
    }
  }, [pokemon]);

  if (!pokemon) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-50" data-testid="pokemon-detail-modal">
      <div className="panel-retro max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Retro modal header */}
        <div className="relative bg-pokered dark:bg-neonpink text-white p-3 sm:p-6 border-b-[3px] border-ink dark:border-neoncyan overflow-hidden">
          <div className="crt-overlay" />
          <div className="flex justify-between items-start relative z-10 gap-3">
            <div>
              <h2 className="font-pixel text-3xl sm:text-5xl capitalize leading-none tracking-wide" data-testid="modal-pokemon-name">
                {pokemon.name}
              </h2>
              <p className="mt-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/90">
                #{pokemon.id.toString().padStart(4, "0")} ▪ {t("modal.dex")}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="modal-close-button"
              className="font-pixel text-3xl bg-white text-pokered hover:bg-cream border-[3px] border-ink w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center shadow-[4px_4px_0_0_#111] dark:shadow-[4px_4px_0_0_#00f0ff] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-all leading-none shrink-0"
            >
              ×
            </button>
          </div>
          
          <div className="mt-4 sm:mt-5 flex flex-wrap gap-2 sm:gap-3 relative z-10">
            <button
              onClick={() => setActiveTab('info')}
              data-testid="tab-pokemon-info"
              className={`btn-retro !text-sm sm:!text-lg !px-2.5 sm:!px-4 ${activeTab === 'info' ? 'btn-primary' : ''}`}
            >
              {t("modal.tab.info")}
            </button>
            <button
              onClick={() => setActiveTab('cards')}
              data-testid="tab-trading-cards"
              className={`btn-retro !text-sm sm:!text-lg !px-2.5 sm:!px-4 ${activeTab === 'cards' ? 'btn-secondary' : ''}`}
            >
              {t("modal.tab.cards")}
            </button>
          </div>
        </div>

        <div className="p-3 sm:p-6 bg-cream dark:bg-nightbg text-ink dark:text-white">
          {activeTab === 'info' && (
            <>
              {loading ? (
                <div className="text-center py-12">
                  <div className="font-pixel text-3xl text-pokered dark:text-neonpink animate-blink">{t("modal.info.loading")}</div>
                </div>
              ) : pokemonData ? (
                <div>
                  <div className="text-center mb-8">
                    <div className="panel-retro inline-block p-5">
                      <img
                        src={pokemonData.sprites?.other?.['official-artwork']?.front_default || pokemonData.sprites?.front_default}
                        alt={pokemon.name}
                        className="w-56 h-56 mx-auto object-contain pixelated"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-5">
                      <div className="panel-retro p-5">
                        <h3 className="font-pixel text-2xl text-pokegreen dark:text-neongreen mb-3">{t("modal.info.basic")}</h3>
                        <div className="space-y-2 font-mono text-sm">
                          <p className="flex justify-between border-b-2 border-dashed border-ink/20 dark:border-white/20 pb-1"><span className="uppercase tracking-widest text-ink/60 dark:text-white/60">{t("modal.info.height")}</span><span className="font-bold">{pokemonData.height / 10} m</span></p>
                          <p className="flex justify-between border-b-2 border-dashed border-ink/20 dark:border-white/20 pb-1"><span className="uppercase tracking-widest text-ink/60 dark:text-white/60">{t("modal.info.weight")}</span><span className="font-bold">{pokemonData.weight / 10} kg</span></p>
                          <p className="flex justify-between"><span className="uppercase tracking-widest text-ink/60 dark:text-white/60">{t("modal.info.baseXp")}</span><span className="font-bold">{pokemonData.base_experience}</span></p>
                        </div>
                      </div>

                      <div className="panel-retro p-5">
                        <h4 className="font-pixel text-2xl text-pokeblue dark:text-neoncyan mb-3">{t("modal.info.types")}</h4>
                        <div className="flex flex-wrap gap-2">
                          {pokemonData.types?.map((type) => (
                            <span
                              key={type.type.name}
                              className="px-3 py-1 font-pixel text-lg uppercase tracking-wide bg-pokeblue dark:bg-neoncyan text-white dark:text-nightbg border-[3px] border-ink dark:border-white"
                            >
                              {type.type.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="panel-retro p-5">
                        <h3 className="font-pixel text-2xl text-pokered dark:text-neonpink mb-3">{t("modal.info.stats")}</h3>
                        <div className="space-y-3">
                          {pokemonData.stats?.map((stat) => {
                            const filled = Math.round((stat.base_stat / 255) * 10);
                            return (
                              <div key={stat.stat.name}>
                                <div className="flex justify-between font-mono text-xs uppercase tracking-widest mb-1">
                                  <span>{stat.stat.name.replace('-', ' ')}</span>
                                  <span className="led-price !px-2 !py-0">{stat.base_stat}</span>
                                </div>
                                <div className="pixel-bar">
                                  {Array.from({ length: 10 }).map((_, i) => (
                                    <span key={i} className={i < filled ? 'on' : ''} />
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="panel-retro p-5">
                      <h3 className="font-pixel text-2xl text-pokegreen dark:text-neongreen mb-3">{t("modal.info.abilities")}</h3>
                      <div className="flex flex-wrap gap-2">
                        {pokemonData.abilities?.map((ability) => (
                          <span
                            key={ability.ability.name}
                            className="px-3 py-1 font-pixel text-lg uppercase tracking-wide bg-white dark:bg-nightsurface text-ink dark:text-neoncyan border-[3px] border-ink dark:border-neoncyan"
                          >
                            {ability.ability.name.replace('-', ' ')}
                            {ability.is_hidden && " ✦"}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="font-pixel text-3xl text-pokered dark:text-neonpink">{t("modal.info.error")}</p>
                </div>
              )}
            </>
          )}

          {activeTab === 'cards' && (
            <TradingCardsSection pokemonName={pokemon.name} />
          )}
        </div>
      </div>
    </div>
  );
};

// ✨ OPTIMIZED: Trading Cards Section with On-Demand Pricing
const TradingCardsSection = ({ pokemonName }) => {
  const { t } = useT();
  console.log(`🎯 COMPONENT LOADED: TradingCardsSection for ${pokemonName}`);
  
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [error, setError] = useState(null);
  const [testResults, setTestResults] = useState('Ready to load cards');
  const [pricesLoaded, setPricesLoaded] = useState(false);
  const [sortOrder, setSortOrder] = useState('default'); // default, price-high, price-low
  
  useEffect(() => {
    console.log(`🔄 useEffect triggered for: ${pokemonName}`);
    if (pokemonName) {
      // Load cards WITHOUT pricing (user clicks button to load prices)
      setTimeout(() => {
        loadCards(false);
      }, 100);
    }
  }, [pokemonName]);

  const loadCards = async (includePricing = false) => {
    const loadType = includePricing ? 'Loading cards with prices...' : 'Loading cards...';
    console.log(`📡 ${loadType} for ${pokemonName}`);
    
    if (includePricing) {
      setLoadingPrices(true);
    } else {
      setLoading(true);
    }
    
    setTestResults(loadType);
    setError(null);
    
    try {
      // Use backend proxy endpoint - Request ALL cards WITHOUT pricing first (fast)
      const backendUrl = getBackendUrl();
      const proxyUrl = `${backendUrl}/api/pokemon-tcg-proxy?q=name:${pokemonName}*&pageSize=250&includePricing=false`;
      
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend proxy error ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.data && data.data.length > 0) {
        console.log(`✅ SUCCESS: Found ${data.data.length} cards for ${pokemonName}`);
        setCards(data.data);
        
        // Check if cards have pricing data (Cardmarket or TCGPlayer)
        const hasPricing = data.data.some(card => 
          (card.cardmarket?.prices?.averageSellPrice) || 
          (card.tcgplayer?.prices?.normal?.market)
        );
        
        if (hasPricing) {
          // Cards already have real pricing from TCGdex!
          setPricesLoaded(true);
          setTestResults(`✅ Found ${data.data.length} cards with Cardmarket prices`);
        } else {
          setTestResults(`✅ Found ${data.data.length} cards`);
        }
        
        // If pricing requested, load prices progressively
        if (includePricing) {
          loadPricesProgressively(data.data, backendUrl);
        } else {
          setLoading(false);
        }
      } else {
        console.log(`❌ No cards found for ${pokemonName}`);
        setCards([]);
        setTestResults(`ℹ️ No cards found for ${pokemonName}`);
        setLoading(false);
        setLoadingPrices(false);
      }
      
    } catch (err) {
      console.error(`💥 API ERROR:`, err);
      
      let errorMessage = err.message || 'Unknown error';
      setError(errorMessage);
      setTestResults(`❌ Error: ${errorMessage}`);
      setCards([]);
      setLoading(false);
      setLoadingPrices(false);
    }
  };

  // Get best available price for a card (checks all TCGPlayer variants + Cardmarket fallback)
  const getCardPrice = (card) => {
    const tcg = card.tcgplayer?.prices;
    if (tcg) {
      // Pick the highest market price across all variants (holofoil, normal, reverseHolofoil, 1stEditionHolofoil, etc.)
      const variants = Object.values(tcg);
      const marketPrices = variants
        .map(v => v?.market ?? v?.mid ?? v?.low)
        .filter(p => typeof p === 'number' && p > 0);
      if (marketPrices.length > 0) {
        return Math.max(...marketPrices);
      }
    }
    // Cardmarket fallback (EUR ≈ USD for sorting purposes)
    const cm = card.cardmarket?.prices;
    if (cm) {
      const cmPrice = cm.averageSellPrice || cm.trendPrice || cm.lowPrice || 0;
      if (cmPrice > 0) return cmPrice;
    }
    return null;
  };

  // Sort cards by price (cards without prices always go to the end)
  const getSortedCards = (cardsToSort) => {
    if (sortOrder === 'default') {
      return cardsToSort;
    }
    
    const sorted = [...cardsToSort].sort((a, b) => {
      const priceA = getCardPrice(a);
      const priceB = getCardPrice(b);

      // Push cards without a price to the bottom in both directions
      if (priceA === null && priceB === null) return 0;
      if (priceA === null) return 1;
      if (priceB === null) return -1;
      
      if (sortOrder === 'price-high') {
        return priceB - priceA; // High to Low
      } else if (sortOrder === 'price-low') {
        return priceA - priceB; // Low to High
      }
      return 0;
    });
    
    return sorted;
  };

  // Fetch ALL prices at once for the Pokemon, then match to cards
  const loadPricesProgressively = async (cardsData, backendUrl) => {
    console.log(`💰 Fetching ALL ${pokemonName} prices from pokemonpricetracker.com...`);
    
    try {
      // Fetch ALL prices for this Pokemon at once
      const bulkPriceUrl = `${backendUrl}/api/pokemon-bulk-prices?pokemonName=${encodeURIComponent(pokemonName)}`;
      const response = await fetch(bulkPriceUrl);
      const bulkData = await response.json();
      
      if (bulkData.success && bulkData.prices) {
        console.log(`✅ Received ${bulkData.prices.length} prices from pokemonpricetracker.com`);
        
        // Match prices to cards - PRECISE MATCHING (each card gets its own price)
        setCards(prevCards => {
          const updatedCards = prevCards.map(card => {
            const cardName = card.name.toLowerCase().trim();
            const cardSet = (card.set?.name || '').toLowerCase().trim();
            const cardNumber = card.id.includes('-') ? card.id.split('-')[1] : '';
            
            // Find the BEST matching price (prioritize exact matches)
            let bestMatch = null;
            let bestMatchScore = 0;
            
            bulkData.prices.forEach(p => {
              const priceName = p.name.toLowerCase().trim();
              const priceSet = (p.setName || '').toLowerCase().trim();
              const priceNumber = (p.cardNumber || '').toLowerCase().trim();
              
              let matchScore = 0;
              
              // Exact name match is required
              if (priceName !== cardName) {
                // Check for variant matches (e.g., "Venusaur ex" vs "Venusaur")
                if (!priceName.includes(cardName) && !cardName.includes(priceName)) {
                  return; // Skip if names don't match at all
                }
              }
              
              // Calculate match score
              if (priceName === cardName) {
                matchScore += 100; // Exact name match
              } else if (priceName.includes(cardName) || cardName.includes(priceName)) {
                matchScore += 50; // Partial name match
              }
              
              // Add points for set match
              if (priceSet && cardSet) {
                if (priceSet === cardSet) {
                  matchScore += 50; // Exact set match
                } else if (priceSet.includes(cardSet) || cardSet.includes(priceSet)) {
                  matchScore += 25; // Partial set match
                }
              }
              
              // Add points for card number match
              if (priceNumber && cardNumber && priceNumber === cardNumber) {
                matchScore += 30; // Card number match
              }
              
              // Update best match if this is better
              if (matchScore > bestMatchScore) {
                bestMatchScore = matchScore;
                bestMatch = p;
              }
            });
            
            // Apply the best matching price to this specific card
            // IMPORTANT: only overwrite tcgplayer / cardmarket when the new data is non-empty,
            // otherwise we'd wipe out the valid TCGdex Cardmarket prices and end up with
            // "Price not available" for cards that actually do have a price.
            if (bestMatch) {
              const newTcg = bestMatch.tcgplayer && bestMatch.tcgplayer.prices?.normal?.market != null
                ? bestMatch.tcgplayer
                : card.tcgplayer;
              const newCm = bestMatch.cardmarket && bestMatch.cardmarket.prices?.averageSellPrice != null
                ? bestMatch.cardmarket
                : card.cardmarket;
              return {
                ...card,
                tcgplayer: newTcg,
                cardmarket: newCm,
              };
            }
            
            return card;
          });
          
          return updatedCards;
        });
        
        setLoadingPrices(false);
        setPricesLoaded(true);
        setError(''); // Clear any error messages
        // Show different message for estimated vs real pricing
        if (bulkData.estimated) {
          setTestResults(`💡 ${bulkData.prices.length} estimated prices loaded (based on card rarity)`);
        } else {
          setTestResults(`✅ All ${bulkData.prices.length} available prices loaded!`);
        }
      } else {
        // Handle different types of errors from backend
        if (bulkData.blocked) {
          setError(`⚠️ API temporarily blocked. ${bulkData.error || 'Please wait and try again.'}`);
        } else if (bulkData.rateLimit) {
          // Rate limit error - provide helpful feedback
          const retryAfter = bulkData.retryAfter || 30;
          setError(`⏱️ Rate limit reached. Please wait ${retryAfter} seconds and try again.`);
          setTestResults(`⏱️ Rate limit - retry in ${retryAfter}s`);
        } else if (bulkData.serverError) {
          // Server error (500) - pricing API is down
          const retryAfter = bulkData.retryAfter || 60;
          setError(`🔧 ${bulkData.error || 'Pricing service temporarily unavailable. Please try again in a few minutes.'}`);
          setTestResults(`🔧 Service unavailable - retry in ${retryAfter}s`);
        } else {
          setError(bulkData.error || 'Failed to fetch prices');
        }
        setLoadingPrices(false);
        if (!bulkData.rateLimit && !bulkData.serverError) {
          setTestResults('❌ Unable to load prices');
        }
      }
    } catch (err) {
      console.error('Error loading bulk prices:', err);
      setLoadingPrices(false);
      
      // Check if it's an API block error
      if (err.message && err.message.includes('blocked')) {
        setError('⚠️ API temporarily blocked. Please wait a few minutes and try again.');
      } else {
        setError('Failed to load prices. Please try again in a few minutes.');
      }
      setTestResults('❌ Unable to load prices at this time');
    }
  };

  console.log(`🔍 RENDER: Loading=${loading}, Cards=${cards.length}, Error=${error}, TestResults=${testResults}`);

  return (
    <div className="p-2" data-testid="trading-cards-section">
      {/* Toolbar */}
      <div className="mb-5">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3">
          <div className="flex flex-wrap gap-2">
            {cards.length > 0 && !pricesLoaded && (
              <button 
                onClick={() => loadCards(true)}
                className="btn-retro btn-secondary disabled:opacity-50"
                disabled={loading || loadingPrices}
                data-testid="load-prices-button"
              >
                {loadingPrices ? t("cards.loadingPrices") : t("cards.loadPrices")}
              </button>
            )}
            
            {pricesLoaded && (
              <div className="led-price text-sm" data-testid="prices-loaded-badge">
                {t("cards.allLoaded")}
              </div>
            )}
          </div>
          
          {cards.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="font-mono text-xs uppercase tracking-widest text-ink/70 dark:text-white/70">{t("cards.sort")}</label>
              <select 
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="retro-select px-3 py-2 font-mono text-sm bg-white dark:bg-nightmuted border-[3px] border-ink dark:border-neoncyan text-ink dark:text-white outline-none"
                data-testid="cards-sort-select"
              >
                <option value="default">{t("cards.sort.default")}</option>
                <option value="price-high">{t("cards.sort.priceHigh")}</option>
                <option value="price-low">{t("cards.sort.priceLow")}</option>
              </select>
            </div>
          )}
        </div>
        
        {loadingPrices && (
          <div className="panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm p-3">
            <p className="font-mono text-xs uppercase tracking-widest text-pokeblue dark:text-neoncyan">
              {t("cards.fetchingNote")}
            </p>
          </div>
        )}
      </div>
      
      {error && (
        <div className="panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm p-3 mb-5 bg-pokered/10 dark:bg-neonpink/10">
          <p className="font-mono text-sm text-pokered dark:text-neonpink">⚠ {error}</p>
        </div>
      )}

      {loading && (
        <div className="text-center py-10">
          <div className="font-pixel text-3xl text-pokered dark:text-neonpink animate-blink" data-testid="cards-loading">
            {t("cards.loading")}
          </div>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest text-ink/60 dark:text-white/60">
            {t("cards.loadingSub", { name: pokemonName })}
          </p>
        </div>
      )}

      {cards.length > 0 && !loading && (
        <div>
          <h4 className="font-pixel text-2xl text-pokegreen dark:text-neongreen mb-3" data-testid="cards-count">
            {t("cards.found", { n: cards.length, name: pokemonName.toUpperCase() })}
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
            {getSortedCards(cards).map((card, index) => (
              <div
                key={card.id || index}
                className="panel-retro !shadow-retro-light-sm dark:!shadow-retro-dark-sm hover:!shadow-retro-light dark:hover:!shadow-retro-dark transition-all duration-150 p-2 flex flex-col"
                data-testid={`trading-card-${index}`}
              >
                <div className="holo-card mb-2 border-[2px] border-ink dark:border-neoncyan/60 bg-white">
                  <img 
                    src={card.images?.small || 'https://via.placeholder.com/200x280/f0f0f0/999?text=Card'} 
                    alt={card.name}
                    loading="lazy"
                    className="w-full h-auto"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/200x280/f0f0f0/999?text=Card';
                    }}
                  />
                </div>
                <h5 className="font-pixel text-base sm:text-lg leading-tight text-ink dark:text-white truncate">{card.name}</h5>
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60 dark:text-white/60 truncate">{card.set?.name}</p>
                {card.rarity && (
                  <span className="mt-1 inline-block self-start font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-pokeblue dark:bg-neoncyan text-white dark:text-nightbg border-2 border-ink dark:border-white">
                    ★ {card.rarity}
                  </span>
                )}
                
                {pricesLoaded && (
                  <div className="mt-2 space-y-1">
                    {card.tcgplayer?.prices?.normal && card.tcgplayer.prices.normal.market ? (
                      <div className="led-price w-full text-[11px] leading-tight">
                        <div className="flex items-center justify-between">
                          <span>$ MARKET</span>
                          <span className="font-bold">${card.tcgplayer.prices.normal.market}</span>
                        </div>
                        {card.tcgplayer.estimated && (
                          <div className="text-[9px] opacity-70 mt-0.5">▌ EST ▌</div>
                        )}
                        <div className="mt-1 grid grid-cols-3 gap-1 text-[9px] opacity-90">
                          {card.tcgplayer.prices.normal.low && <span>L ${card.tcgplayer.prices.normal.low}</span>}
                          {card.tcgplayer.prices.normal.mid && <span>M ${card.tcgplayer.prices.normal.mid}</span>}
                          {card.tcgplayer.prices.normal.high && <span>H ${card.tcgplayer.prices.normal.high}</span>}
                        </div>
                      </div>
                    ) : (
                      <div className="led-price w-full text-[10px] !text-red-400">
                        {t("cards.noPrice")}
                      </div>
                    )}
                    {card.cardmarket?.prices?.averageSellPrice && (
                      <div className="font-mono text-[11px] font-semibold px-2 py-1 bg-purple-600 text-white border-2 border-ink dark:border-white tracking-tight flex items-center justify-between">
                        <span>€ CM</span>
                        <span>€{card.cardmarket.prices.averageSellPrice}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {cards.length === 0 && !loading && !error && testResults.includes('No cards found') && (
        <div className="panel-retro p-6 text-center" data-testid="no-cards-found">
          <div className="font-pixel text-4xl text-pokered dark:text-neonpink animate-blink">{t("cards.none.title")}</div>
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-ink/60 dark:text-white/60">
            {t("cards.none.subtitle", { name: pokemonName })}
          </p>
        </div>
      )}
    </div>
  );
};

// ✨ Enhanced Card Detail Modal
const CardDetailModal = ({ card, onClose }) => {
  const [collection, setCollection] = useState([]);
  const [priceSource, setPriceSource] = useState('tcgplayer');

  useEffect(() => {
    const savedCollection = JSON.parse(localStorage.getItem('pokemonCardCollection') || '[]');
    setCollection(savedCollection);
  }, []);

  if (!card) return null;

  const addToCollection = () => {
    const newItem = {
      id: card.id,
      name: card.name,
      set: card.set?.name,
      rarity: card.rarity,
      image: card.images?.small,
      addedAt: new Date().toISOString()
    };
    
    const updatedCollection = [...collection, newItem];
    setCollection(updatedCollection);
    localStorage.setItem('pokemonCardCollection', JSON.stringify(updatedCollection));
    alert('🎉 Card added to your collection!');
  };

  const isInCollection = collection.some(item => item.id === card.id);

  const formatPriceDetails = (tcgplayer, cardmarket = null) => {
    if (priceSource === 'cardmarket') {
      // Show CardMarket prices
      if (cardmarket?.prices) {
        const prices = cardmarket.prices;
        return (
          <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <h5 className="font-bold text-green-800 mb-2">🇪🇺 CardMarket Prices</h5>
            <div className="text-sm text-gray-700 grid grid-cols-2 gap-2">
              {prices.averageSellPrice && <span className="bg-white px-2 py-1 rounded">Average: <span className="font-bold">€{prices.averageSellPrice.toFixed(2)}</span></span>}
              {prices.lowPrice && <span className="bg-white px-2 py-1 rounded">Low: <span className="font-bold">€{prices.lowPrice.toFixed(2)}</span></span>}
              {prices.trendPrice && <span className="bg-white px-2 py-1 rounded">Trend: <span className="font-bold">€{prices.trendPrice.toFixed(2)}</span></span>}
              {prices.germanProLow && <span className="bg-green-200 px-2 py-1 rounded font-bold">Pro Low: €{prices.germanProLow.toFixed(2)}</span>}
            </div>
            {cardmarket.url && (
              <a 
                href={cardmarket.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-full font-bold hover:from-green-700 hover:to-green-800 transition-all shadow-lg text-sm"
              >
                🛒 View on CardMarket →
              </a>
            )}
          </div>
        );
      } else if (tcgplayer?.prices) {
        // Fallback: Convert TCGPlayer to EUR
        const prices = tcgplayer.prices;
        const priceTypes = Object.entries(prices);
        return priceTypes.map(([type, priceData]) => (
          <div key={type} className="mb-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <h5 className="font-bold capitalize text-yellow-800 mb-2">💰 {type.replace(/([A-Z])/g, ' $1')} (Converted to EUR)</h5>
            <div className="text-sm text-gray-700 grid grid-cols-2 gap-2">
              {priceData.low && <span className="bg-white px-2 py-1 rounded">Low: <span className="font-bold">€{(priceData.low * 0.85).toFixed(2)}</span></span>}
              {priceData.mid && <span className="bg-white px-2 py-1 rounded">Mid: <span className="font-bold">€{(priceData.mid * 0.85).toFixed(2)}</span></span>}
              {priceData.high && <span className="bg-white px-2 py-1 rounded">High: <span className="font-bold">€{(priceData.high * 0.85).toFixed(2)}</span></span>}
              {priceData.market && <span className="bg-yellow-200 px-2 py-1 rounded font-bold">Market: €{(priceData.market * 0.85).toFixed(2)} (est.)</span>}
            </div>
          </div>
        ));
      }
    } else {
      // Show TCGPlayer prices
      if (tcgplayer?.prices) {
        const priceTypes = Object.entries(tcgplayer.prices);
        return priceTypes.map(([type, prices]) => (
          <div key={type} className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <h5 className="font-bold capitalize text-green-800 mb-2">💎 {type.replace(/([A-Z])/g, ' $1')}</h5>
            <div className="text-sm text-gray-700 grid grid-cols-2 gap-2">
              {prices.low && <span className="bg-white px-2 py-1 rounded">Low: <span className="font-bold">${prices.low}</span></span>}
              {prices.mid && <span className="bg-white px-2 py-1 rounded">Mid: <span className="font-bold">${prices.mid}</span></span>}
              {prices.high && <span className="bg-white px-2 py-1 rounded">High: <span className="font-bold">${prices.high}</span></span>}
              {prices.market && <span className="bg-green-200 px-2 py-1 rounded font-bold">Market: ${prices.market}</span>}
            </div>
          </div>
        ));
      }
    }
    
    return <p className="text-gray-500 italic">No pricing data available</p>;
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4" 
      style={{ zIndex: 10000 }}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className="bg-gradient-to-br from-yellow-50 via-white to-blue-50 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-4 border-yellow-400"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Pokemon-themed Header */}
        <div className="bg-gradient-to-r from-red-600 via-red-700 to-blue-700 text-white p-6 rounded-t-3xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-transparent to-blue-400/20"></div>
          <div className="flex justify-between items-start relative z-10">
            <div>
              <h2 className="text-3xl font-bold mb-2 text-yellow-100">🎴 {card.name}</h2>
              <p className="text-yellow-200 font-semibold">{card.set?.name} • {card.set?.series}</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-yellow-200 hover:text-white text-3xl font-bold bg-red-800 hover:bg-red-900 rounded-full w-10 h-10 flex items-center justify-center transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-br from-yellow-100 to-blue-100 p-4 rounded-2xl border-3 border-yellow-400 shadow-xl">
                <img
                  src={card.images?.large || card.images?.small}
                  alt={card.name}
                  className="w-full max-w-sm mx-auto rounded-xl shadow-lg border-2 border-white"
                />
              </div>
              
              <button
                onClick={addToCollection}
                disabled={isInCollection}
                className={`mt-6 px-8 py-4 rounded-full font-bold text-lg shadow-lg transition-all transform hover:scale-105 ${
                  isInCollection 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 border-3 border-green-800'
                }`}
              >
                {isInCollection ? '✅ In Collection' : '📦 Add to Collection'}
              </button>
            </div>

            <div>
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-4 rounded-2xl border-2 border-blue-300">
                  <h3 className="text-2xl font-bold text-blue-800 mb-3">📋 Card Details</h3>
                  <div className="space-y-2 text-sm">
                    <p className="bg-white p-2 rounded-lg"><span className="font-bold">Set:</span> {card.set?.name}</p>
                    <p className="bg-white p-2 rounded-lg"><span className="font-bold">Series:</span> {card.set?.series}</p>
                    <p className="bg-white p-2 rounded-lg"><span className="font-bold">Release Date:</span> {card.set?.releaseDate}</p>
                    {card.rarity && <p className="bg-white p-2 rounded-lg"><span className="font-bold">Rarity:</span> ⭐ {card.rarity}</p>}
                    {card.artist && <p className="bg-white p-2 rounded-lg"><span className="font-bold">Artist:</span> 🎨 {card.artist}</p>}
                  </div>
                </div>

                {(card.tcgplayer?.prices || card.cardmarket?.prices) && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-2xl font-bold text-green-800">💰 Market Prices</h3>
                      
                      {/* Price Source Toggle */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPriceSource('tcgplayer')}
                          className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${
                            priceSource === 'tcgplayer' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          💰 TCGPlayer
                        </button>
                        <button
                          onClick={() => setPriceSource('cardmarket')}
                          className={`px-4 py-2 rounded-full font-bold text-sm transition-colors ${
                            priceSource === 'cardmarket' 
                              ? 'bg-green-600 text-white' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          🇪🇺 CardMarket
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      {formatPriceDetails(card.tcgplayer, card.cardmarket)}
                    </div>
                    
                    {/* Show both links if available */}
                    <div className="flex gap-3 mt-4">
                      {card.tcgplayer?.url && (
                        <a 
                          href={card.tcgplayer.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-full font-bold hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg text-sm"
                        >
                          🛒 TCGPlayer →
                        </a>
                      )}
                      {card.cardmarket?.url && (
                        <a 
                          href={card.cardmarket.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-full font-bold hover:from-green-700 hover:to-green-800 transition-all shadow-lg text-sm"
                        >
                          🛒 CardMarket →
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {card.attacks && card.attacks.length > 0 && (
                  <div className="bg-gradient-to-r from-red-100 to-orange-100 p-4 rounded-2xl border-2 border-red-300">
                    <h3 className="text-2xl font-bold text-red-800 mb-3">⚔️ Attacks</h3>
                    <div className="space-y-3">
                      {card.attacks.map((attack, index) => (
                        <div key={index} className="bg-white p-3 rounded-lg border border-red-200">
                          <h4 className="font-bold text-red-700">💥 {attack.name}</h4>
                          {attack.damage && <p className="text-sm text-gray-600">⚡ Damage: <span className="font-bold">{attack.damage}</span></p>}
                          {attack.text && <p className="text-sm mt-1">{attack.text}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(card.weaknesses || card.resistances) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {card.weaknesses && (
                      <div>
                        <h4 className="font-bold text-red-800 mb-2">🔥 Weaknesses</h4>
                        <div className="flex flex-wrap gap-2">
                          {card.weaknesses.map((weakness, index) => (
                            <span key={index} className="px-3 py-1 bg-red-200 text-red-800 rounded-full text-sm font-bold border border-red-300">
                              {weakness.type} {weakness.value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {card.resistances && (
                      <div>
                        <h4 className="font-bold text-blue-800 mb-2">🛡️ Resistances</h4>
                        <div className="flex flex-wrap gap-2">
                          {card.resistances.map((resistance, index) => (
                            <span key={index} className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm font-bold border border-blue-300">
                              {resistance.type} {resistance.value}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const Pokedex = () => {
  const { t } = useT();
  const [pokemon, setPokemon] = useState([]);
  const [allPokemon, setAllPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPokemon, setSelectedPokemon] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showHighScores, setShowHighScores] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [priceSource, setPriceSource] = useState('tcgplayer');
  const [user, setUser] = useState({
    name: '',
    email: '',
    isLoggedIn: false,
    level: 1,
    badges: 0,
    pokemonCaught: 0,
    cardsCollected: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    generation: '',
    minId: '',
    maxId: '',
    sortBy: 'id'
  });
  const pokemonPerPage = 150;

  // Load user data from localStorage
  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem('l3dex_user') || '{}');
    if (Object.keys(savedUser).length > 0) {
      setUser(savedUser);
    }
  }, []);

  useEffect(() => {
    const fetchAllPokemon = async () => {
      try {
        setLoading(true);
        // Fetch Pokemon up to 1025 (excluding 1026-1302)
        const response = await axios.get(`${API_BASE}/pokemon?limit=1025&offset=0`);
        
        const pokemonData = response.data.results.map((poke, index) => {
          const id = index + 1;
          return {
            id,
            name: poke.name,
            sprite: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${id}.png`,
            url: poke.url,
            types: [] // Will be populated when needed for filtering
          };
        });
        
        setAllPokemon(pokemonData);
        setPokemon(pokemonData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching pokemon:", error);
        setLoading(false);
      }
    };

    fetchAllPokemon();
  }, []);

  // Filter and sort pokemon
  useEffect(() => {
    const applyFilters = async () => {
      let filtered = [...allPokemon];

      if (filters.search) {
        filtered = filtered.filter(poke =>
          poke.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          poke.id.toString().includes(filters.search)
        );
      }

      // ✅ FIXED: Type Filter - Fetch types only when type filter is active
      if (filters.type) {
        const typePromises = filtered.map(async (poke) => {
          try {
            const response = await axios.get(`${API_BASE}/pokemon/${poke.id}`);
            const pokemonTypes = response.data.types?.map(type => type.type.name) || [];
            return { ...poke, types: pokemonTypes, matchesType: pokemonTypes.includes(filters.type) };
          } catch (error) {
            console.error(`Error fetching types for ${poke.name}:`, error);
            return { ...poke, types: [], matchesType: false };
          }
        });

        const pokemonWithTypes = await Promise.all(typePromises);
        filtered = pokemonWithTypes.filter(poke => poke.matchesType);
      }

      if (filters.generation) {
        const genIndex = parseInt(filters.generation) - 1;
        const [min, max] = POKEMON_GENERATIONS[genIndex].range;
        filtered = filtered.filter(poke => poke.id >= min && poke.id <= max);
      }

      if (filters.minId) {
        filtered = filtered.filter(poke => poke.id >= parseInt(filters.minId));
      }
      if (filters.maxId) {
        filtered = filtered.filter(poke => poke.id <= parseInt(filters.maxId));
      }

      if (filters.sortBy === 'name') {
        filtered.sort((a, b) => a.name.localeCompare(b.name));
      } else if (filters.sortBy === 'nameDesc') {
        filtered.sort((a, b) => b.name.localeCompare(a.name));
      } else {
        filtered.sort((a, b) => a.id - b.id);
      }

      setPokemon(filtered);
      setCurrentPage(1);
    };

    applyFilters();
  }, [filters, allPokemon]);

  // Pagination
  const totalPages = Math.ceil(pokemon.length / pokemonPerPage);
  const startIndex = (currentPage - 1) * pokemonPerPage;
  const paginatedPokemon = pokemon.slice(startIndex, startIndex + pokemonPerPage);

  const handlePokemonSelect = (poke) => {
    setSelectedPokemon(poke);
  };

  const handleCloseDetail = () => {
    setSelectedPokemon(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('l3dex_user', JSON.stringify(userData));
    setShowProfile(false);
  };

  const handleLogout = () => {
    setUser({
      name: '',
      email: '',
      isLoggedIn: false,
      level: 1,
      badges: 0,
      pokemonCaught: 0,
      cardsCollected: 0
    });
    localStorage.removeItem('l3dex_user');
  };

  if (loading) {
    return <RetroLoading label={t("loading.label")} />;
  }

  return (
    <div className="min-h-screen bg-cream dark:bg-nightbg text-ink dark:text-white transition-colors">
      <RetroHeader
        allPokemon={allPokemon}
        onPokemonSelect={handlePokemonSelect}
        user={user}
        onLoginClick={() => setShowProfile(true)}
      />

      <HeroSection
        onCta={() => {
          const el = document.getElementById("pokedex-grid-anchor");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        onHighScores={() => setShowHighScores(true)}
      />

      <TrendingStrip allPokemon={allPokemon} onSelect={handlePokemonSelect} backendUrl={getBackendUrl()} />

      {/* Content */}
      <div id="pokedex-grid-anchor" className="max-w-7xl mx-auto p-3 sm:p-6">
        <PokemonFilters 
          filters={filters} 
          onFiltersChange={handleFiltersChange}
          totalCount={pokemon.length}
        />

        {pokemon.length > 0 ? (
          <>
            <PokemonGrid pokemon={paginatedPokemon} onPokemonSelect={handlePokemonSelect} />
            
            {/* Retro Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center py-8 gap-2 sm:gap-3 px-2 sm:px-0 flex-wrap" data-testid="pagination">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn-retro disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pagination-prev"
                >
                  {t("pagination.prev")}
                </button>
                
                <div className="flex gap-2">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    if (page <= totalPages) {
                      const isActive = currentPage === page;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          data-testid={`pagination-page-${page}`}
                          className={`btn-retro !px-3 !py-2 !text-xl ${
                            isActive ? "btn-primary" : ""
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                    return null;
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-retro btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pagination-next"
                >
                  {t("pagination.next")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20" data-testid="no-results">
            <div className="font-pixel text-4xl text-pokered dark:text-neonpink animate-blink">
              {t("noresults.title")}
            </div>
            <p className="mt-3 font-mono text-sm uppercase tracking-widest text-ink/60 dark:text-white/60">
              {t("noresults.subtitle")}
            </p>
          </div>
        )}
      </div>

      {/* Pokemon Detail Modal */}
      {selectedPokemon && (
        <PokemonDetail 
          pokemon={selectedPokemon} 
          onClose={handleCloseDetail}
          priceSource={priceSource}
          onPriceSourceChange={setPriceSource}
        />
      )}

      {/* User Profile Modal */}
      {showProfile && (
        <UserProfile 
          user={user}
          onClose={() => setShowProfile(false)}
          onLogin={handleLogin}
          onLogout={handleLogout}
        />
      )}

      {/* High Scores leaderboard */}
      <HighScoresModal
        open={showHighScores}
        onClose={() => setShowHighScores(false)}
        allPokemon={allPokemon}
        onSelectPokemon={handlePokemonSelect}
        backendUrl={getBackendUrl()}
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Pokedex />
    </div>
  );
}

export default App;